/**
 * Static localized strings for the printed book's front-matter pages
 * (TOC heading, publisher letter, imprint).
 *
 * Mirrors backend/src/services/bookStrings.ts. Keep both in sync.
 *
 * Keyed by `templates.language` codes used in the DB: ru, kz, en, uk, tr.
 * `kz` here is the template-level Kazakhstan code — *not* the i18n `kk`.
 *
 * These render the contents of the printed book itself, not the UI around
 * it, so they follow the *template's* language regardless of the user's
 * current UI language.
 */
export type BookLang = "ru" | "kz" | "en" | "uk" | "tr";

export interface BookStrings {
  imprint: string;             // "Lovestoria"
  toc: string;                 // "Contents"
  forewordPre: string;         // "Foreword" — rendered as the big bold uppercase title
  forewordParagraphs: string[]; // Body paragraphs (paragraph breaks rendered via white-space pre-line)
  publisherSignature: string;  // "Издательство Lovestoria"
  /** Placeholder shown on the title page when the book title is unset. */
  titlePlaceholder: string;
  /** Preposition before recipient name on title page, e.g. "for {name}". */
  receiverFor: (name: string) => string;
  /** "Глава 1" / "Chapter 1" / "1 Бөлім" / "Розділ 1" — chapter section heading. */
  chapterLabel: (n: number) => string;
}

const IMPRINT = "Lovestoria";

export const BOOK_STRINGS: Record<BookLang, BookStrings> = {
  ru: {
    imprint: IMPRINT,
    toc: "Содержание",
    forewordPre: "Предисловие",
    forewordParagraphs: [
      "Есть люди, которые достойны целой книги. Выходит, вы — один из них.",
      "Если вы держите эту книгу в руках, значит, ваш близкий человек решил, что ваше имя должно быть напечатано на бумаге, а ваша история — переплетена в обложку.",
      "Дальше — главы о вас. О мелочах, знакомых только самым близким, и о моментах, которые иначе со временем растворились бы.",
      "Не торопитесь. Перечитывайте, замедляйтесь, возвращайтесь. Эти страницы стоят того, чтобы провести с ними время.",
    ],
    publisherSignature: `Издательство ${IMPRINT}`,
    titlePlaceholder: "название книги",
    receiverFor: (name) => `для ${name}`,
    chapterLabel: (n) => `Глава ${n}`,
  },

  en: {
    imprint: IMPRINT,
    toc: "Contents",
    forewordPre: "Foreword",
    forewordParagraphs: [
      "Some people deserve an entire book. As it turns out, you are one of them.",
      "If you are holding this book, it means someone close to you decided your name belongs printed on paper and your story belongs bound into a cover.",
      "Ahead are chapters about you. About small things only those nearest to you know, and about moments that would otherwise dissolve with time.",
      "Take your time. Reread, slow down, come back. These pages are worth lingering over.",
    ],
    publisherSignature: `${IMPRINT} Press`,
    titlePlaceholder: "book title",
    receiverFor: (name) => `for ${name}`,
    chapterLabel: (n) => `Chapter ${n}`,
  },

  uk: {
    imprint: IMPRINT,
    toc: "Зміст",
    forewordPre: "Передмова",
    forewordParagraphs: [
      "Є люди, які варті цілої книги. Виходить, ви — один із них.",
      "Якщо ви тримаєте цю книгу в руках, значить, ваша близька людина вирішила, що ваше ім'я має бути надруковане на папері, а ваша історія — переплетена в обкладинку.",
      "Далі — розділи про вас. Про дрібниці, відомі лише найближчим, і про моменти, які інакше з часом розчинилися б.",
      "Не поспішайте. Перечитуйте, сповільнюйтесь, повертайтеся. Ці сторінки варті того, щоб провести з ними час.",
    ],
    publisherSignature: `Видавництво ${IMPRINT}`,
    titlePlaceholder: "назва книги",
    receiverFor: (name) => `для ${name}`,
    chapterLabel: (n) => `Розділ ${n}`,
  },

  kz: {
    imprint: IMPRINT,
    toc: "Мазмұны",
    forewordPre: "Алғы сөз",
    forewordParagraphs: [
      "Бір кітапқа лайық адамдар бар. Сонымен, сіз де солардың бірісіз.",
      "Егер бұл кітап сіздің қолыңызда болса, демек, жақын адамыңыз сіздің есіміңіз қағазға басылып, әңгімеңіз мұқабаға тігілуге лайық деп шешкен.",
      "Әрі қарай — сіз туралы тараулар. Тек ең жақын адамдарға ғана белгілі ұсақ-түйектер және уақыт өте келе ұмыт боп кететін сәттер туралы.",
      "Асықпаңыз. Қайта оқыңыз, баяуласыңыз, қайтып келіңіз. Бұл беттер сізбен бірге уақыт өткізуге татиды.",
    ],
    publisherSignature: `${IMPRINT} баспасы`,
    titlePlaceholder: "кітап атауы",
    receiverFor: (name) => `${name} үшін`,
    chapterLabel: (n) => `${n} Бөлім`,
  },

  tr: {
    imprint: IMPRINT,
    toc: "İçindekiler",
    forewordPre: "Önsöz",
    forewordParagraphs: [
      "Bütün bir kitabı hak eden insanlar vardır. Görünen o ki siz de onlardan birisiniz.",
      "Bu kitabı elinizde tutuyorsanız, sevdiğiniz biri adınızın kâğıda basılmayı, hikâyenizin de bir kapağın içinde ciltlenmeyi hak ettiğine karar vermiş demektir.",
      "Sırada sizin hakkınızda bölümler var. Yalnızca en yakınlarınızın bildiği küçük ayrıntılar ve zamanla silinip gidebilecek anlar hakkında.",
      "Acele etmeyin. Yeniden okuyun, yavaşlayın, tekrar dönün. Bu sayfalar, onlarla vakit geçirmeye değer.",
    ],
    publisherSignature: `${IMPRINT} Yayınevi`,
    titlePlaceholder: "kitabın adı",
    receiverFor: (name) => `${name} için`,
    chapterLabel: (n) => `${n}. Bölüm`,
  },
};

export function resolveBookLang(lang: string | null | undefined): BookLang {
  if (lang === "en" || lang === "uk" || lang === "kz" || lang === "tr") return lang;
  return "ru";
}

export function getBookStrings(lang: string | null | undefined): BookStrings {
  return BOOK_STRINGS[resolveBookLang(lang)];
}
